import { useState, useEffect } from 'react';
import { Link2, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { CreateConnectionDialog } from './CreateConnectionDialog';
import { captureError } from '@/lib/sentry';

interface Connection {
  id: string;
  contact_id_1: string;
  contact_id_2: string;
  relationship_type: string;
  created_at: string;
  contact: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    is_company: boolean;
  };
}

interface ContactConnectionsSidebarProps {
  contactId: string;
}

export function ContactConnectionsSidebar({ contactId }: ContactConnectionsSidebarProps) {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { toast } = useToast();

  const fetchConnections = async () => {
    try {
      // Fetch connections where the current contact is either contact_1 or contact_2
      const { data: connectionsData, error: contactsError } = await supabase
        .from('connections')
        .select(`
          id,
          contact_id_1,
          contact_id_2,
          relationship_type,
          created_at,
          contact:contacts!connections_contact_id_2_fkey (
            id,
            first_name,
            last_name,
            company_name,
            is_company
          )
        `)
        .eq('contact_id_1', contactId);

      if (contactsError) throw contactsError;

      // Also fetch reverse connections
      const { data: reverseData, error: reverseError } = await supabase
        .from('connections')
        .select(`
          id,
          contact_id_1,
          contact_id_2,
          relationship_type,
          created_at,
          contact:contacts!connections_contact_id_1_fkey (
            id,
            first_name,
            last_name,
            company_name,
            is_company
          )
        `)
        .eq('contact_id_2', contactId);

      if (reverseError) throw reverseError;

      const allConnections = [...(connectionsData || []), ...(reverseData || [])].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setConnections(allConnections as unknown as Connection[]);
    } catch (error) {
      console.error('Error fetching connections:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, [contactId]);

  const removeConnection = async (connectionId: string) => {
    try {
      const { error } = await supabase
        .from('connections')
        .delete()
        .eq('id', connectionId);

      if (error) throw error;

      toast({
        description: "Connection removed",
      });

      fetchConnections();
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-connections-sidebar-remove-connection' });
      toast({
        title: "Error",
        description: "Failed to remove connection",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="h-4 w-4 text-primary/70" />
          <h3 className="font-semibold text-sm">Connections</h3>
        </div>
        <div className="text-xs text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="h-4 w-4 text-primary/70" />
        <h3 className="font-semibold text-sm">Connections</h3>
      </div>

      <div className="space-y-1">
        {connections.map((connection) => {
          const connectedContact = connection.contact;

          return (
            <div
              key={connection.id}
              className="group flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors"
            >
              <div className="min-w-0 flex-1">
                {(() => {
                  const c = connectedContact;
                  const isCompanyAsPrimary =
                    c.is_company === true ||
                    (!c.first_name && !c.last_name && !!c.company_name);
                  const personName = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();

                  return (
                    <>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link
                          to={`/contacts/${c.id}`}
                          className="text-xs font-medium hover:underline truncate"
                        >
                          {isCompanyAsPrimary
                            ? c.company_name
                            : (personName || c.company_name || '(no name)')}
                        </Link>
                        {connection.relationship_type && (
                          <Badge variant="outline" className="text-[10px] capitalize px-1.5 py-0">
                            {connection.relationship_type}
                          </Badge>
                        )}
                      </div>
                      {!isCompanyAsPrimary && c.company_name && (
                        <div className="text-[10px] text-muted-foreground truncate">
                          {c.company_name}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeConnection(connection.id)}
                  className="h-5 w-5 text-destructive"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          );
        })}

        {connections.length === 0 && (
          <div className="text-xs text-muted-foreground py-2">
            No connections yet
          </div>
        )}
      </div>

      <Button
        variant="ghost"
        className="w-full justify-start text-muted-foreground hover:text-foreground text-xs h-7 mt-2"
        onClick={() => setCreateDialogOpen(true)}
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add Connection
      </Button>

      <CreateConnectionDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        contactId={contactId}
        onSuccess={fetchConnections}
      />
    </div>
  );
}
